import React from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  SelectChangeEvent,
  useTheme
} from '@mui/material';
import {
  IconIdentifier,
  TemplateCategory
} from '@/types/templateCategoryTypes';
import { 
  FirstAidKit, 
  Briefcase, 
  GraduationCap, 
  FileText,
  Gavel,
  ShieldCheck,
  Bank
} from '@phosphor-icons/react';

// --- Icon Utility ---
export const getCategoryIcon = (
  iconIdentifier: IconIdentifier | '' | undefined
): React.ReactElement => {
  const size = 16;
  const weight = 'regular' as const;
  
  switch (iconIdentifier) {
    case IconIdentifier.Medical:
      return <FirstAidKit size={size} weight={weight} />;
    case IconIdentifier.Education:
      return <GraduationCap size={size} weight={weight} />;
    case IconIdentifier.Business:
      return <Briefcase size={size} weight={weight} />;
    case IconIdentifier.General:
      return <FileText size={size} weight={weight} />;
    default:
      return <FileText size={size} weight={weight} />;
  }
};

// --- Component Props ---
interface TemplateCategoryPickerProps {
  value: number | '';
  onChange: (event: SelectChangeEvent<number | ''>) => void;
  categories: TemplateCategory[];
  isLoadingCategories: boolean;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  label?: string;
  required?: boolean;
  name?: string;
  id?: string;
  labelId?: string;
  fullWidth?: boolean;
  margin?: 'none' | 'dense' | 'normal';
  variant?: 'standard' | 'outlined' | 'filled';
}

// --- Reusable Template Category Picker Component ---
const TemplateCategoryPicker: React.FC<TemplateCategoryPickerProps> = ({
  value,
  onChange,
  categories,
  isLoadingCategories,
  error = false,
  helperText = '',
  disabled = false,
  label = 'Template Category',
  required = false,
  name = 'template-category',
  id = 'template-category-select',
  labelId = 'template-category-select-label',
  fullWidth = true,
  margin = 'dense',
  variant = 'outlined'
}) => {
  const theme = useTheme();
  
  return (
    <FormControl
      fullWidth={fullWidth}
      margin={margin}
      required={required}
      error={error}
      disabled={disabled || isLoadingCategories}
      variant={variant}
    >
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select
        labelId={labelId}
        id={id}
        name={name}
        value={value}
        label={label}
        onChange={onChange}
        MenuProps={{
          PaperProps: {
            sx: {
              mt: 1,
              borderRadius: '8px',
              boxShadow: theme.palette.mode === 'dark'
                ? '0 4px 20px rgba(0,0,0,0.5)'
                : '0 2px 8px rgba(0,0,0,0.15)',
              '& .MuiMenuItem-root': {
                fontSize: '14px',
                py: 1.25,
                px: 2,
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6'
                }
              }
            }
          }
        }}
        renderValue={(selectedValue: number | '') => {
          if (isLoadingCategories && value === '') return <em>Loading...</em>;
          if (selectedValue === '') {
            return <em>Select a category</em>;
          }
          const category = categories.find((c) => c.id === selectedValue);
          return (
            <span style={{ display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left' }}>
              {category ? getCategoryIcon(category.iconIdentifier) : null}
              {category ? category.name : 'Select a category'}
            </span>
          );
        }}
      >
        {isLoadingCategories ? (
          <MenuItem value="" disabled>
            <em>Loading categories...</em>
          </MenuItem>
        ) : categories.length === 0 ? (
          <MenuItem value="" disabled>
            <em>No categories available</em>
          </MenuItem>
        ) : (
          [
            // Optional: Add a "None" or "Select a category" option if not required or to allow clearing
            // <MenuItem value="" key="empty-select">
            //   <em>Select a category</em>
            // </MenuItem>,
            ...categories.map((cat) => (
              <MenuItem key={cat.id} value={cat.id}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {getCategoryIcon(cat.iconIdentifier)}
                  {cat.name}
                </span>
              </MenuItem>
            ))
          ]
        )}
      </Select>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

export default TemplateCategoryPicker;
